import type { ArdoqId } from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { isTruthy } from '@ardoq/common-helpers';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import type {
  ComponentTypeInventoryDatasourceSelection,
  InventoryDatasourceSelection,
  WorkspaceInventoryDatasourceSelection,
} from '../types';
import type { SingleWorkspaceDatasourceInformationProps } from './SingleWorkspaceDatasourceInformation';
import type { SingleComponentTypeDatasourceInformationProps } from './SingleComponentTypeDatasourceInformation';
import type { MultipleWorkspacesDatasourceInformationProps } from './MultipleWorkspacesDatasourceInformation';
import type {
  ComponentTypeGroup,
  MultipleComponentTypesDatasourceInformationProps,
} from './MultipleComponentTypesDatasourceInformation';
import type { GetWorkspaceManagerLinkProps } from './types';

type GetDataSourceInformationDataArgs = {
  datasourceSelection: InventoryDatasourceSelection;
  scopeData: EnhancedScopeData;
  canManageWorkspace: (workspaceId: ArdoqId) => boolean;
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps;
  locale: Locale;
  i18nLocale: I18nLocale;
};

type SingleWorkspaceDatasourceInformationData = {
  dataSourceType: 'single-workspace';
  props: SingleWorkspaceDatasourceInformationProps;
};
type SingleComponentTypeDatasourceInformationData = {
  dataSourceType: 'single-component-type';
  props: SingleComponentTypeDatasourceInformationProps;
};
type MultipleWorkspacesDatasourceInformationData = {
  dataSourceType: 'multiple-workspaces';
  props: MultipleWorkspacesDatasourceInformationProps;
};
type MultipleComponentTypesDatasourceInformationData = {
  dataSourceType: 'multiple-component-types';
  props: MultipleComponentTypesDatasourceInformationProps;
};

export type DatasourceInformationData =
  | SingleWorkspaceDatasourceInformationData
  | SingleComponentTypeDatasourceInformationData
  | MultipleWorkspacesDatasourceInformationData
  | MultipleComponentTypesDatasourceInformationData
  | null;

export const getDataSourceInformationData = ({
  datasourceSelection,
  scopeData,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  locale,
  i18nLocale,
}: GetDataSourceInformationDataArgs): DatasourceInformationData => {
  switch (datasourceSelection.dataSourceType) {
    case 'dataset':
    case 'myApplicationType':
    case 'report':
      return null;

    case 'componentType': {
      return getComponentTypeDataSourceInformationData(
        datasourceSelection,
        scopeData,
        canManageWorkspace,
        getWorkspaceManagerLinkProps,
        i18nLocale
      );
    }

    case 'workspace': {
      return getWorkspaceDataSourceInformationData(
        datasourceSelection,
        scopeData,
        canManageWorkspace,
        getWorkspaceManagerLinkProps,
        locale,
        i18nLocale
      );
    }

    default:
      datasourceSelection satisfies never;
      return null;
  }
};

const getWorkspaceDataSourceInformationData = (
  datasourceSelection: WorkspaceInventoryDatasourceSelection,
  scopeData: EnhancedScopeData,
  canManageWorkspace: (workspaceId: ArdoqId) => boolean,
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps,
  locale: Locale,
  i18nLocale: I18nLocale
):
  | SingleWorkspaceDatasourceInformationData
  | MultipleWorkspacesDatasourceInformationData
  | null => {
  const workspaces = datasourceSelection.selectedWorkspaceIds
    .map(workspaceId => scopeData.workspacesById[workspaceId])
    .filter(isTruthy);
  if (workspaces.length === 0) {
    return null;
  }
  if (workspaces.length === 1) {
    return {
      dataSourceType: 'single-workspace',
      props: {
        workspace: workspaces[0],
        canManageWorkspace,
        getWorkspaceManagerLinkProps,
        locale,
        i18nLocale,
      },
    };
  }
  return {
    dataSourceType: 'multiple-workspaces',
    props: {
      workspaces,
      canManageWorkspace,
      getWorkspaceManagerLinkProps,
      locale,
      i18nLocale,
    },
  };
};

const getComponentTypeDataSourceInformationData = (
  datasourceSelection: ComponentTypeInventoryDatasourceSelection,
  scopeData: EnhancedScopeData,
  canManageWorkspace: (workspaceId: ArdoqId) => boolean,
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps,
  i18nLocale: I18nLocale
):
  | SingleComponentTypeDatasourceInformationData
  | MultipleComponentTypesDatasourceInformationData
  | null => {
  if (datasourceSelection.componentTypeNames.length > 1) {
    return getMultipleComponentTypesDataSourceInformationData(
      datasourceSelection,
      scopeData,
      canManageWorkspace,
      getWorkspaceManagerLinkProps,
      i18nLocale
    );
  }
  return getSingleComponentTypeDataSourceInformationData(
    datasourceSelection,
    scopeData,
    canManageWorkspace,
    getWorkspaceManagerLinkProps,
    i18nLocale
  );
};

const getSingleComponentTypeDataSourceInformationData = (
  datasourceSelection: ComponentTypeInventoryDatasourceSelection,
  scopeData: EnhancedScopeData,
  canManageWorkspace: (workspaceId: ArdoqId) => boolean,
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps,
  i18nLocale: I18nLocale
): SingleComponentTypeDatasourceInformationData | null => {
  if (datasourceSelection.componentTypeNames.length !== 1) {
    return null;
  }
  const componentTypeGroup = getComponentTypeGroup(
    datasourceSelection.componentTypeNames[0],
    scopeData,
    datasourceSelection.workspaceIds
  );
  if (!componentTypeGroup) {
    return null;
  }
  return {
    dataSourceType: 'single-component-type',
    props: {
      ...componentTypeGroup,
      canManageWorkspace,
      getWorkspaceManagerLinkProps,
      i18nLocale,
    },
  };
};

/**
 * Resolves a component type name to the types (one per model/workspace) and the
 * workspaces it features in, scoped to the selected workspaces.
 */
const getComponentTypeGroup = (
  componentTypeName: string,
  scopeData: EnhancedScopeData,
  selectedWorkspaceIds: ArdoqId[]
): ComponentTypeGroup | null => {
  const selectedWorkspaceIdSet = new Set(selectedWorkspaceIds);
  const matchesByModel = Object.entries(scopeData.typesByModelId)
    .map(([modelId, modelTypes]) => ({
      componentTypes: Object.values(modelTypes.componentTypesById).filter(
        componentType => componentType.name === componentTypeName
      ),
      workspaceId: enhancedScopeDataOperations.getWorkspaceIdByComponentModelId(
        scopeData,
        modelId
      ),
    }))
    .filter(
      match =>
        match.componentTypes.length > 0 &&
        match.workspaceId !== null &&
        selectedWorkspaceIdSet.has(match.workspaceId)
    );

  if (!matchesByModel.length) {
    return null;
  }

  const componentTypes = matchesByModel.flatMap(match => match.componentTypes);
  const workspaces = matchesByModel
    .map(match => match.workspaceId)
    .filter(isTruthy)
    .map(workspaceId => scopeData.workspacesById[workspaceId])
    .filter(isTruthy);

  return { componentTypes, workspaces };
};

const getMultipleComponentTypesDataSourceInformationData = (
  datasourceSelection: ComponentTypeInventoryDatasourceSelection,
  scopeData: EnhancedScopeData,
  canManageWorkspace: (workspaceId: ArdoqId) => boolean,
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps,
  i18nLocale: I18nLocale
): MultipleComponentTypesDatasourceInformationData | null => {
  const componentTypeGroups = datasourceSelection.componentTypeNames
    .map(componentTypeName =>
      getComponentTypeGroup(
        componentTypeName,
        scopeData,
        datasourceSelection.workspaceIds
      )
    )
    .filter(isTruthy);
  if (!componentTypeGroups.length) {
    return null;
  }
  return {
    dataSourceType: 'multiple-component-types',
    props: {
      componentTypeGroups,
      canManageWorkspace,
      getWorkspaceManagerLinkProps,
      i18nLocale,
    },
  };
};
